import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { updateInvoiceSentAndPaidDetails } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	invoiceSentAt: Date | null;
	invoicePaidAt: Date | null;
	recipientInformation: string|null;
}

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	invoiceData: Invoice;
};

const UpdateInvoiceStatusForm: FunctionComponent<Props> = ({
	onCancel,
	onSuccess,
	invoiceData,
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const isSelfBilledInvoice = invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice;

	const schema = useMemo(() => {
		return yup.object({
			invoiceSentAt: yup.date().required().default(null),
			invoicePaidAt: yup.date().nullable().default(null),
			recipientInformation: yup.string().nullable().default(null),
		});
	}, []);
	const defaultValues = useMemo(() => {
		const transactionDate = isSelfBilledInvoice ? invoiceData.invoiceReceivedAt : invoiceData.invoiceSentAt;
		const recipientOrSenderInformation = isSelfBilledInvoice ? invoiceData.senderInformation : invoiceData.recipientInformation;
		return {
			invoicePaidAt: invoiceData?.invoicePaidAt ? new Date(invoiceData.invoicePaidAt) : null,
			invoiceSentAt: transactionDate ? new Date(transactionDate) : null,
			recipientInformation: recipientOrSenderInformation || "",
		};
	}, [invoiceData.invoicePaidAt, invoiceData.invoiceReceivedAt, invoiceData.invoiceSentAt, invoiceData.recipientInformation, invoiceData.senderInformation, isSelfBilledInvoice]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData = {
				invoicePaidAt: data.invoicePaidAt ? formatDateToYYYYMMDD(data.invoicePaidAt) : null,
				invoiceSentAt: data.invoiceSentAt ? formatDateToYYYYMMDD(data.invoiceSentAt) : null,
				recipientInformation: data.recipientInformation,
			};

			await updateInvoiceSentAndPaidDetails({ invoiceId: invoiceData.id, updateData });
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, onSuccess]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnungsstatus ändern</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Speichern fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title={isSelfBilledInvoice ? "Eingangsdatum / Absender":"Versanddatum / Empfänger"}>
						<div className="space-y-4">
							<DatePickerInput name="invoiceSentAt"
											 control={control}
											 label={isSelfBilledInvoice ? "Eingangsdatum":"Versanddatum"} />
							<Input control={control}
								   name="recipientInformation"
								   label={isSelfBilledInvoice ? "Absender":"Versendet an"}
								   placeholder="Name, E-Mail, Firma..." />
						</div>
					</FormSection>
					<FormSection title="Buchungsdatum">
						<DatePickerInput name="invoicePaidAt"
										 control={control} />
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateInvoiceStatusForm;
